<template>
  <button
    class="base-btn"
    :class="[
      `base-btn--${variant}`,
      `base-btn--${size}`,
      { 'base-btn--monochrome': monochrome },
      { 'base-btn--block': block, 'base-btn--loading': loading },
      { 'base-btn--attention': attention && !disabled && !loading },
    ]"
    :type="type"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
    @click="onClick"
  >
    <span v-if="loading" class="base-btn__spinner" aria-hidden="true" />
    <i v-else-if="icon"
class="base-btn__icon zmdi"
:class="icon"
aria-hidden="true" />
    <span class="base-btn__label"><slot /></span>
  </button>
</template>

<script setup lang="ts">
type Variant = 'primary' | 'inverted' | 'ghost' | 'token' | 'danger' | 'danger-outline'
type Size = 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    type?: 'button' | 'submit' | 'reset'
    block?: boolean
    disabled?: boolean
    loading?: boolean
    /** Flat auth/landing controls; no accent, gradient or shadow. */
    monochrome?: boolean
    /** Opt-in sheen for an available primary action; never changes its colour. */
    attention?: boolean
    /** Optional leading zmdi icon class, e.g. "zmdi-flash". */
    icon?: string
  }>(),
  {
    variant: 'primary',
    size: 'md',
    type: 'button',
    block: false,
    disabled: false,
    loading: false,
    icon: '',
  },
)

const emit = defineEmits<{ (e: 'click', ev: MouseEvent): void }>()

function onClick(ev: MouseEvent): void {
  if (props.disabled || props.loading) return
  emit('click', ev)
}
</script>

<style scoped lang="scss">
.base-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--p-space-3);
  border: 1px solid transparent;
  border-radius: var(--ds-radius-control);
  font-family: var(--p-font-ui);
  font-weight: var(--p-weight-bold);
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background var(--p-motion-base) var(--p-ease),
    color var(--p-motion-base) var(--p-ease),
    border-color var(--p-motion-base) var(--p-ease),
    opacity var(--p-motion-base) var(--p-ease);

  &:focus-visible {
    outline: none;
    box-shadow: var(--ds-focus-ring-shadow);
  }

  &:disabled,
  &.base-btn--loading {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &--block {
    display: flex;
    width: 100%;
    white-space: normal;
  }

  &--attention {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    &::after {
      content: '';
      position: absolute;
      inset-block: 0;
      left: -60%;
      width: 45%;
      pointer-events: none;
      background: linear-gradient(110deg, transparent, rgb(255 255 255 / 24%), transparent);
      transform: skewX(-15deg);
      animation: base-btn-sheen 6s ease-in-out infinite;
    }
  }

  // ─── Sizes ───────────────────────────────────────────────
  &--sm {
    padding: var(--p-space-2) var(--p-space-4);
    font-size: var(--p-text-12);
  }
  &--md {
    padding: var(--p-space-3) var(--p-space-5);
    font-size: var(--p-text-14);
  }
  &--lg {
    padding: var(--p-space-4) var(--p-space-6);
    font-size: var(--p-text-16);
  }

  // ─── Variants ────────────────────────────────────────────
  // primary — dominant accent CTA
  &--primary {
    background: var(--ds-accent);
    color: var(--ds-text-on-accent);
    text-transform: uppercase;
    letter-spacing: 0.2px;

    &:hover:not(:disabled) {
      background: var(--ds-accent-strong);
    }
  }

  // inverted — high-contrast polarity flip (white-on-dark / dark-on-light)
  &--inverted {
    background: var(--ds-text);
    color: var(--ds-surface-canvas);
    text-transform: uppercase;
    letter-spacing: 0.2px;

    &:hover:not(:disabled) {
      opacity: 0.88;
    }
  }

  // ghost — secondary, transparent with hover tint
  &--ghost {
    background: transparent;
    color: var(--ds-text);
    border-color: var(--ds-border);

    &:hover:not(:disabled) {
      background: var(--ds-accent-soft);
      color: var(--ds-accent);
      border-color: var(--ds-accent);
    }
  }

  // danger — destructive actions
  &--danger {
    background: var(--ds-danger-action-bg);
    color: var(--ds-danger-action-text);
    border-color: var(--ds-danger-action-bg);

    &:hover:not(:disabled) {
      opacity: 0.88;
    }
  }

  &--danger-outline {
    background: transparent;
    color: var(--ds-danger-action-outline);
    border-color: currentColor;
  }

  // token — violet pill / tag button
  &--token {
    padding-inline: var(--p-space-5);
    background: var(--ds-accent-soft);
    color: var(--ds-accent);
    border-color: var(--ds-accent);
    border-radius: var(--ds-radius-control-pill);
    text-transform: none;

    &:hover:not(:disabled) {
      background: var(--ds-accent);
      color: var(--ds-text-on-accent);
    }
  }

  &__icon {
    font-size: 1.1em;
    line-height: 1;
  }

  &.base-btn--monochrome {
    background: #101010 !important;
    color: #fff !important;
    border: 1px solid var(--ds-text) !important;
    background-image: none !important;
    box-shadow: none !important;
    text-transform: none;
    letter-spacing: normal;

    &:hover, &:focus, &:active {
      background: #101010 !important;
      color: #fff !important;
      box-shadow: none !important;
      transform: none !important;
    }

    &:focus-visible {
      outline: 2px solid var(--ds-text);
      outline-offset: 3px;
    }

    &.base-btn--ghost {
      background: #fff !important;
      color: #101010 !important;
      border-color: #101010 !important;
    }
  }

  &__spinner {
    width: 1em;
    height: 1em;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: var(--p-radius-full);
    animation: base-btn-spin 0.7s linear infinite;
  }
}

@keyframes base-btn-sheen {
  0%, 65% { left: -60%; }
  90%, 100% { left: 120%; }
}
@media (prefers-reduced-motion: reduce) {
  .base-btn--attention::after { display: none; animation: none; }
}
@keyframes base-btn-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
